import { css } from '@emotion/css';
import React, { ChangeEvent, FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { ReactComponent as Balloon } from '../../assets/images/balloon.svg';
import { RootState } from '../../features';
import { actions } from '../../features/chat';
import { fontFamily } from '../../lib/font-family';
import { sendException } from '../../lib/google-analytics';
import { replaceAllSpaces } from '../../lib/utils';
import { reveal, shake } from '../shared/keyframe-animations';
import { usePrefersReducedMotion } from '../shared/usePrefersReducedMotion';

const shakeAnim = `${shake} 0.2s cubic-bezier(0.36, 0.07, 0.19, 0.97) both`;
const noneAnim = `${reveal} 0.1s linear both`;

interface ChatFormProps {
  onSubmit: (message: string) => void;
}

const chatFormStyle = css`
  display: flex;
  justify-content: center;
  align-items: center;
  background: white;
  border-radius: 32px;
  height: 56px;
  position: relative;
  overflow: hidden;

  .message {
    width: 100%;
    border: none;
    font-size: 20px;
    white-space: nowrap;
    outline: none;
    word-break: keep-all;
    flex: 1 1 auto;
    display: inline-block;
    caret-color: #09ae9e;
    color: #4d3906;
    overflow: hidden;
    margin-left: 12px;
  }
  .message::placeholder {
    color: rgba(105, 90, 76, 0.3);
  }
  .submit {
    flex: 0 0 auto;
    display: flex;
    justify-content: center;
    align-items: center;
    appearance: none;
    border: none;
    outline: none;
    box-shadow: none;
    color: white;
    border: 4px solid #ffffff;
    background: #00b6a9;
    position: relative;
    width: 72px;
    height: 100%;
    border-radius: 32px;
    cursor: pointer;
    transition: transform 0.15s cubic-bezier(0.17, 0.89, 0.32, 1.49), background 0.1s;
    :active {
      transform: scale(0.9);
    }
    :disabled {
      background: #cdc7bb;
      color: #fff9e2;
      pointer-events: none;
    }
  }
`;

const canvas: HTMLCanvasElement = document.createElement('canvas');
const context = canvas.getContext('2d');

// Safariでソフトウェアキーボードが非表示になったときにスクロール位置を戻すworkaround
const handleBlur = () => window.scrollTo(0, 0);

export const ChatForm: React.FC<ChatFormProps> = ({ onSubmit }) => {
  const { t, i18n } = useTranslation('chat');
  const { message, isSending, warnTypes } = useSelector((state: RootState) => state.chat);
  const dispatch = useDispatch();
  const location = useLocation();
  const routeMatch = useRouteMatch();

  const prefersReducedMotion = usePrefersReducedMotion();

  const [warnedAt, setWarnedAt] = useState(0);

  useEffect(() => {
    if (!context) {
      sendException('canvas context for measuring chat text is not available');
      return;
    }
    context.font = `normal 48px ${fontFamily(i18n.language)}`;
    const replaced = replaceAllSpaces(message);
    const { width } = context.measureText(replaced); // to count space
    dispatch(actions.updateMessageWidth({ width }));
  }, [message, dispatch, i18n.language]);

  const focusRef = useCallback(
    (node: HTMLInputElement | null) => {
      if (!node || !routeMatch) {
        return;
      }
      if (routeMatch.url === location.pathname) {
        node.focus();
      } else {
        node.blur();
      }
    },
    [location.pathname, routeMatch]
  );

  const handleMessageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const m = e.target.value || '';
    dispatch(actions.updateMessage(m));
  };

  const handleAnimationEnd = () => {
    setWarnedAt(message.length);
  };

  const handleSubmit = useCallback(
    (ev: FormEvent) => {
      ev.preventDefault();
      if (message && !isSending) {
        onSubmit(message);
      }
    },
    [message, isSending, onSubmit]
  );

  return (
    <form
      className={chatFormStyle}
      onSubmit={handleSubmit}
      onAnimationEnd={handleAnimationEnd}
      style={{
        animation:
          !prefersReducedMotion && warnTypes.length > 0 && warnedAt !== message.length
            ? shakeAnim
            : noneAnim
      }}
    >
      <input
        ref={focusRef}
        className="message"
        type="text"
        disabled={isSending}
        onBlur={handleBlur}
        onChange={handleMessageChange}
        value={message}
      />
      <button
        type="submit"
        disabled={!message || isSending}
        className="submit"
        aria-label={t('send')}
      >
        <Balloon />
      </button>
    </form>
  );
};
